import type { ReactNode, Ref, SelectHTMLAttributes } from "react";
import "./text-field.css";
import "./select.css";

type Option = { value: string; label: string; disabled?: boolean };

type Props = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id" | "value" | "onChange" | "children" | "aria-invalid" | "aria-describedby"
> & {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  /** Può contenere un link (per esempio a «Ruoli e permessi»): resta comunque legato al campo. */
  hint?: ReactNode;
  error?: string;
  /** Un motivo scritto fuori dal campo (per esempio un avviso sotto), legato anche lui al campo. */
  describedBy?: string;
  /** L'etichetta resta per gli screen reader ma non si vede (un select in una cella di tabella). */
  hideLabel?: boolean;
  className?: string;
  /** Arriva al `<select>` (in React 19 `ref` è una prop), per spostare il focus dopo un errore. */
  ref?: Ref<HTMLSelectElement>;
};

// Come TextField: classe base, `aria-invalid` e `aria-describedby` sono del componente, le prop native
// non possono sovrascriverle. Il suggerimento e l'errore restano scritti e legati al campo; un motivo scritto
// altrove si lega con `describedBy`, come in Checkbox.
export function Select({ id, label, value, onChange, options, hint, error, describedBy: elsewhere, hideLabel, className, ...rest }: Props) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null, elsewhere].filter(Boolean).join(" ");
  return (
    <div className="g-field">
      <label className={hideLabel ? "g-field__label g-visually-hidden" : "g-field__label"} htmlFor={id}>{label}</label>
      <select
        id={id}
        {...rest}
        className={className ? `g-select ${className}` : "g-select"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy || undefined}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>
        ))}
      </select>
      {hint ? <p id={hintId} className="g-field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="g-field__error" role="alert">{error}</p> : null}
    </div>
  );
}
